/**
 * Resolve S3 object keys to the backend image proxy (`GET /image-proxy?key=`).
 *
 * Authenticated users: URLs are HMAC-signed (see signedImageProxyCache.js).
 * Public QR pages: API returns pre-signed URLs in key/display_url fields.
 */

import {
  applyEntityImagePatchToEntity,
  withEntityImageCacheBust,
} from '../components/entityImage/entityImageListSync';
import { getCachedSignedProxyUrl, ensureSignedProxyUrls } from './signedImageProxyCache';
import { extractMediaKey, extractS3KeyFromUrl } from './s3ObjectKey';

export { decodeS3ObjectKey, extractMediaKey, extractS3KeyFromUrl } from './s3ObjectKey';

export function getApiBaseUrl() {
  return (window?.env?.REACT_APP_BASE_URL || process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
}

/**
 * URL handed to pdf.js. The viewer fetches the file itself.
 * A signed URL on the API port is cross-origin: deployed sites are on the CORS
 * list, the local SPA (any port, via the dev proxy) is not. Locally, rewrite
 * to a same-origin `/image-proxy` path so the dev server proxies the request.
 */
export function pdfViewerSrc(url) {
  if (!url) {
    return url;
  }
  const base = getApiBaseUrl();
  if (base && !base.startsWith('/')) {
    return url;
  }
  return toSameOriginImageProxyUrl(url);
}

/**
 * Rewrite absolute `/image-proxy?...` URLs to a same-app-origin path.
 * Needed so local CRA (proxy) and canvas export can fetch without cross-origin CORS/CORP issues.
 */
export function toSameOriginImageProxyUrl(url) {
  if (!url || typeof url !== 'string') {
    return url;
  }
  if (url.startsWith('blob:') || url.startsWith('data:') || url.startsWith('/')) {
    return url;
  }
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.href : 'http://localhost');
    if (parsed.pathname.includes('image-proxy')) {
      return `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    /* ignore */
  }
  return url;
}

export function imageProxyUrlFromKey(key) {
  if (!key) return null;
  if (typeof key === 'string' && key.includes('/image-proxy?')) {
    return key;
  }
  const cached = getCachedSignedProxyUrl(key);
  if (cached) {
    return cached;
  }
  if (typeof window !== 'undefined' && localStorage.getItem('token')) {
    ensureSignedProxyUrls([key], { silent: true });
    return null;
  }
  const base = getApiBaseUrl();
  if (!base) return null;
  return `${base}/image-proxy?key=${encodeURIComponent(key)}`;
}

export function imageProxyUrl({ key, displayUrl } = {}) {
  const resolvedKey = key || extractS3KeyFromUrl(displayUrl);
  return imageProxyUrlFromKey(resolvedKey);
}

/** Pass through local/static URLs; proxy S3 object URLs (images, PDF, video). */
export function toMediaSrc(url, fallback = '') {
  return toImageSrc(url, fallback);
}

/** Pass through local/static URLs; proxy S3 URLs. Never return a raw S3 URL (bucket is private). */
export function toImageSrc(url, fallback = '') {
  if (!url) return fallback;
  if (
    url.startsWith('data:') ||
    url.startsWith('blob:') ||
    url.startsWith('/') ||
    url.includes('/image-proxy?') ||
    !url.includes('amazonaws.com')
  ) {
    return url;
  }
  return imageProxyUrl({ displayUrl: url }) || fallback;
}

/**
 * Resolve a media URL for fetchers that cannot send a JWT (`<img>`, `<video>`, pdf.js).
 * Waits for an HMAC-signed proxy URL. Never returns an amazonaws.com URL.
 */
export async function resolveSignedMediaSrc(url, fallback = '') {
  if (!url) return fallback;
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }

  const looksLikeUrl =
    url.includes('://') || url.startsWith('/') || url.startsWith('data:') || url.startsWith('blob:');
  const key = extractMediaKey(url) || (!looksLikeUrl ? url : null);
  if (key && typeof window !== 'undefined' && localStorage.getItem('token')) {
    await ensureSignedProxyUrls([key], { silent: true });
    const signed = getCachedSignedProxyUrl(key);
    if (signed) {
      return signed;
    }
    const base = getApiBaseUrl();
    return base ? `${base}/image-proxy?key=${encodeURIComponent(key)}` : fallback;
  }

  return toImageSrc(url, fallback);
}

const VARIANT_FIELDS = {
  picto: { keys: ['key_picto', 'key'], urls: ['display_url_picto', 'display_url'] },
  small: { keys: ['key_small', 'key'], urls: ['display_url_small', 'display_url'] },
  medium: { keys: ['key_medium', 'key'], urls: ['display_url_medium', 'display_url'] },
  default: { keys: ['key'], urls: ['display_url'] },
};

export function documentHasVariant(doc, variant = 'default') {
  if (!doc) return false;
  const { keys, urls } = VARIANT_FIELDS[variant] || VARIANT_FIELDS.default;
  return keys.some((field) => Boolean(doc[field])) || urls.some((field) => Boolean(doc[field]));
}

export function imageProxyUrlFromDocument(doc, { variant = 'default' } = {}) {
  if (!doc) return null;
  const { keys, urls } = VARIANT_FIELDS[variant] || VARIANT_FIELDS.default;

  for (const field of urls) {
    const value = doc[field];
    if (typeof value === 'string' && value.includes('/image-proxy?')) {
      return value;
    }
  }
  for (const field of keys) {
    const proxy = imageProxyUrlFromKey(doc[field]);
    if (proxy) return proxy;
  }
  for (const field of urls) {
    const proxy = imageProxyUrl({ displayUrl: doc[field] });
    if (proxy) return proxy;
  }
  return null;
}

export function singleDocumentImageUrl(doc, { variant = 'default', fallback = null } = {}) {
  return imageProxyUrlFromDocument(doc, { variant }) || fallback;
}

function pickFromTypedDocuments(documents, pickVariant) {
  if (!documents?.length) return null;
  const cropped = documents.find((doc) => doc.type === 'cropped');
  const original = documents.find((doc) => doc.type === 'original');
  return pickVariant(cropped) || pickVariant(original) || null;
}

export function typedDocumentsImageUrl(documents, { size = 'default', fallback = null } = {}) {
  const pick = (doc) => {
    if (!doc) return null;
    if (size === 'picto') {
      return imageProxyUrlFromDocument(doc, { variant: 'picto' }) || imageProxyUrlFromDocument(doc, { variant: 'default' });
    }
    if (size === 'small') {
      return (
        imageProxyUrlFromDocument(doc, { variant: 'small' }) ||
        imageProxyUrlFromDocument(doc, { variant: 'default' })
      );
    }
    if (size === 'medium') {
      return (
        imageProxyUrlFromDocument(doc, { variant: 'medium' }) ||
        imageProxyUrlFromDocument(doc, { variant: 'default' })
      );
    }
    return imageProxyUrlFromDocument(doc, { variant: 'default' });
  };

  return pickFromTypedDocuments(documents, pick) || fallback;
}

export function typedDocumentsHaveImage(documents, { size = 'default' } = {}) {
  if (!documents?.length) return false;
  const cropped = documents.find((doc) => doc.type === 'cropped');
  const original = documents.find((doc) => doc.type === 'original');

  if (size === 'picto') {
    return documentHasVariant(cropped, 'picto') || documentHasVariant(original, 'default');
  }
  if (size === 'medium') {
    return documentHasVariant(cropped, 'medium') || documentHasVariant(cropped, 'default') || documentHasVariant(original, 'default');
  }
  if (size === 'small') {
    return documentHasVariant(cropped, 'small') || documentHasVariant(cropped, 'default') || documentHasVariant(original, 'default');
  }
  return documentHasVariant(cropped, 'default') || documentHasVariant(original, 'default');
}

/** Single document object or typed document array (machine_document, user_document, …). */
export function resolveEntityImage(documentField, { size = 'default', fallback = null } = {}) {
  if (Array.isArray(documentField)) {
    return typedDocumentsImageUrl(documentField, { size, fallback });
  }
  const variant = size === 'picto' ? 'picto' : size === 'small' ? 'small' : size === 'medium' ? 'medium' : 'default';
  return singleDocumentImageUrl(documentField, { variant, fallback });
}

const MACHINE_IMAGE_KEY_FIELDS = {
  picto: 'key_picto',
  small: 'key_small',
  medium: 'key_medium',
  default: 'key_full',
  source: 'key_source',
};

const MACHINE_IMAGE_DISPLAY_FIELDS = {
  picto: 'display_url_picto',
  small: 'display_url_small',
  medium: 'display_url_medium',
  default: 'display_url',
};

/**
 * Signed display URLs cover picto / small / medium / full (`display_url` = `key_full`).
 * There is no `display_url_source`: `key_source` is the immutable original used only
 * by the editor. `size: 'source'` must never fall back to `display_url` — that
 * regression (6.0.3) reopened the cropper on the already-cropped full variant.
 */
function signedDisplayUrlFromImage(image, size) {
  if (size === 'source') {
    return null;
  }
  const field = MACHINE_IMAGE_DISPLAY_FIELDS[size] || MACHINE_IMAGE_DISPLAY_FIELDS.default;
  const value = image?.[field];
  return typeof value === 'string' && value.includes('/image-proxy?') ? value : null;
}

/** Active row from API shape `machine_image: [...]` (max one active). */
export function activeMachineImage(entity) {
  const rows = entity?.machine_image;
  if (!rows?.length) return null;
  return rows[0];
}

/** Resolve machine photo URL — signed `display_url*` from the API, else `key*` via proxy cache. */
export function machineImageUrl(entityOrImage, { size = 'default', fallback = null } = {}) {
  const entity = applyEntityImagePatchToEntity(entityOrImage);
  const image = entity?.key_source != null ? entity : activeMachineImage(entity);
  if (!image) return fallback;
  const fromDisplay = signedDisplayUrlFromImage(image, size);
  const field = MACHINE_IMAGE_KEY_FIELDS[size] || MACHINE_IMAGE_KEY_FIELDS.default;
  const url = fromDisplay || imageProxyUrlFromKey(image[field]) || fallback;
  return withEntityImageCacheBust(url, entityOrImage?.id);
}

export function machineHasImage(entityOrImage, { size = 'small' } = {}) {
  return Boolean(machineImageUrl(entityOrImage, { size, fallback: null }));
}

const DOMAIN_IMAGE_KEY_FIELDS = {
  picto: 'key_picto',
  small: 'key_small',
  medium: 'key_medium',
  default: 'key_full',
  source: 'key_source',
};

/** Active row from API shape `{entity}_image: [...]` (max one active). */
export function activeDomainImage(entity, imageField) {
  const rows = entity?.[imageField];
  if (!rows?.length) return null;
  return rows[0];
}

/** Resolve URL from a domain `*_image` row or entity with `{field}_image` array. */
export function domainImageUrl(entityOrImage, { imageField, size = 'default', fallback = null } = {}) {
  const entity = applyEntityImagePatchToEntity(entityOrImage);
  const image =
    entity?.key_source != null
      ? entity
      : imageField
        ? activeDomainImage(entity, imageField)
        : null;
  if (!image) return fallback;
  const fromDisplay = signedDisplayUrlFromImage(image, size);
  const field = DOMAIN_IMAGE_KEY_FIELDS[size] || DOMAIN_IMAGE_KEY_FIELDS.default;
  const url = fromDisplay || imageProxyUrlFromKey(image[field]) || fallback;
  return withEntityImageCacheBust(url, entityOrImage?.id);
}

export function domainImageHasImage(entity, { imageField, size = 'medium' } = {}) {
  return Boolean(domainImageUrl(entity, { imageField, size, fallback: null }));
}

/** Prefer canonical `*_image`; fallback legacy `*_document` during Phase 3–4 (object or typed array). */
export function resolveEntityPhotoUrl(
  entity,
  { imageField, documentField, size = 'default', fallback = null } = {},
) {
  const patched = applyEntityImagePatchToEntity(entity);
  const fromImage = domainImageUrl(patched, { imageField, size, fallback: null });
  if (fromImage) return fromImage;
  const doc = documentField ? patched?.[documentField] : null;
  return withEntityImageCacheBust(resolveEntityImage(doc, { size, fallback }), entity?.id);
}

export function entityPhotoHasImage(
  entity,
  { imageField, documentField, size = 'medium' } = {},
) {
  const patched = applyEntityImagePatchToEntity(entity);
  return (
    domainImageHasImage(patched, { imageField, size }) ||
    entityHasImage(patched?.[documentField], { size })
  );
}

export function entityHasImage(documentField, { size = 'default' } = {}) {
  if (Array.isArray(documentField)) {
    return typedDocumentsHaveImage(documentField, { size });
  }
  const variant = size === 'picto' ? 'picto' : size === 'small' ? 'small' : size === 'medium' ? 'medium' : 'default';
  return documentHasVariant(documentField, variant);
}

/** Cropped dual-crop row (`type === 'cropped'`) for legacy re-edit metadata. */
export function activeLegacyCroppedDocument(documentField) {
  if (!Array.isArray(documentField)) {
    return null;
  }
  return documentField.find((doc) => doc?.type === 'cropped') || null;
}

/** True when entity has a legacy original (or any typed photo) but no `*_image` row. */
export function entityHasLegacyDocumentPhoto(entity, { imageField, documentField } = {}) {
  if (activeDomainImage(entity, imageField)) {
    return false;
  }
  return entityHasImage(entity?.[documentField], { size: 'medium' });
}
